// ————————————————————————————————————————————————————————————
// src/components/ui.jsx — CliniFlow UI System v3
// Clinical Avant-Garde · Manrope + Inter
//
// Componentes base que usan CSS vars del design system.
// Funcionan en dark y light mode sin ningún cambio.
//
// EXPORTS:
//   Button, Badge, Input, Spinner
//   Select, Textarea, Checkbox, Tabs
//   Modal, ConfirmDialog, confirm (singleton)
//   Skeleton, SkeletonText, SkeletonAvatar, SkeletonCard
//   SkeletonKPICard, SkeletonTable, SkeletonCalendar
//   EmptyState
//   Toast, ToastContainer, useToast, toast (singleton)
// ————————————————————————————————————————————————————————————

import React, {
  useState, useEffect, useCallback, useRef, createContext, useContext,
} from 'react';

// ———————————————————————————————————————
// SPINNER
// ———————————————————————————————————————
export function Spinner({ size = 16, color = 'var(--brand-s)' }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth="2.5"
      style={{ animation: 'cf-spin 0.7s linear infinite', flexShrink: 0 }}
    >
      <style>{`@keyframes cf-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

// ———————————————————————————————————————
// BUTTON
// Variants: primary | secondary | ghost | danger | warning
// Sizes:    sm | md (default) | lg
// ———————————————————————————————————————
const BUTTON_VARIANTS = {
  primary: {
    background: 'linear-gradient(135deg, var(--brand-p), var(--primary-container, #1a2b3c))',
    color: '#ffffff',
    border: 'none',
    hoverBg: 'linear-gradient(135deg, var(--primary-container, #1a2b3c), var(--brand-p))',
    hoverOpacity: 0.92,
  },
  secondary: {
    background: 'transparent',
    color: 'var(--brand-s)',
    border: '1px solid var(--ghost-border)',
    hoverBg: 'var(--brand-s-sub)',
    hoverOpacity: 1,
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-2)',
    border: '1px solid var(--ghost-border)',
    hoverBg: 'var(--bg-hover)',
    hoverOpacity: 1,
  },
  tertiary: {
    background: 'var(--tertiary-container, #570c00)',
    color: 'var(--on-tertiary-container, #f36242)',
    border: 'none',
    hoverBg: '#6b1000',
    hoverOpacity: 1,
  },
  danger: {
    background: 'var(--danger-sub, rgba(239,68,68,.1))',
    color: 'var(--danger, #EF4444)',
    border: 'none',
    hoverBg: 'var(--danger-sub, rgba(239,68,68,.15))',
    hoverOpacity: 1,
  },
  warning: {
    background: 'var(--warning-sub, rgba(245,158,11,.1))',
    color: 'var(--warning, #F59E0B)',
    border: 'none',
    hoverBg: 'var(--warning-sub, rgba(245,158,11,.15))',
    hoverOpacity: 1,
  },
};

const BUTTON_SIZES = {
  sm: { height: 30, padding: '0 10px', fontSize: 11, borderRadius: 10, iconSize: 13 },
  md: { height: 36, padding: '0 14px', fontSize: 12, borderRadius: 12, iconSize: 15 },
  lg: { height: 42, padding: '0 20px', fontSize: 14, borderRadius: 12, iconSize: 16 },
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon: Icon,
  iconRight: IconRight,
  onClick,
  style: customStyle,
  type = 'button',
  ...rest
}) {
  const v = BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary;
  const s = BUTTON_SIZES[size] || BUTTON_SIZES.md;
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: s.height,
        padding: s.padding,
        fontSize: s.fontSize,
        fontWeight: 600,
        borderRadius: s.borderRadius,
        border: v.border,
        background: hovered ? v.hoverBg : v.background,
        color: v.color,
        opacity: isDisabled ? 0.5 : (hovered ? v.hoverOpacity : 1),
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.12s ease, opacity 0.12s ease, transform 0.08s ease',
        transform: hovered && !isDisabled ? 'translateY(-1px)' : 'translateY(0)',
        fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        userSelect: 'none',
        ...customStyle,
      }}
      {...rest}
    >
      {loading
        ? <Spinner size={s.iconSize} color={v.color} />
        : Icon && <Icon size={s.iconSize} strokeWidth={1.5} />
      }
      {children}
      {!loading && IconRight && <IconRight size={s.iconSize} strokeWidth={1.5} />}
    </button>
  );
}

// ———————————————————————————————————————
// BADGE
// Variants: brand | success | warning | danger | neutral | info
// ———————————————————————————————————————
const BADGE_VARIANTS = {
  brand:   { bg: 'var(--brand-p-sub)',                          color: 'var(--brand-p-readable)' },
  success: { bg: 'var(--success-sub, rgba(34,197,94,.1))',      color: 'var(--success, #22C55E)' },
  warning: { bg: 'var(--warning-sub, rgba(245,158,11,.1))',     color: 'var(--warning, #F59E0B)' },
  danger:  { bg: 'var(--danger-sub, rgba(239,68,68,.1))',       color: 'var(--danger, #EF4444)' },
  neutral: { bg: 'var(--bg-elevated)',                           color: 'var(--text-2)' },
  info:    { bg: 'rgba(59,130,246,.1)',                          color: '#60A5FA' },
  tertiary: { bg: 'var(--tertiary-sub)',                         color: 'var(--tertiary)' },
};

export function Badge({
  children,
  variant = 'neutral',
  dot = false,
  style: customStyle,
}) {
  const v = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '2px 8px',
      borderRadius: 20,
      fontSize: 9,
      fontWeight: 800,
      background: v.bg,
      color: v.color,
      border: 'none',
      textTransform: 'uppercase',
      letterSpacing: '.04em',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
      ...customStyle,
    }}>
      {dot && (
        <span style={{
          width: 5, height: 5, borderRadius: '50%',
          background: v.color, flexShrink: 0,
        }} />
      )}
      {children}
    </span>
  );
}

// ———————————————————————————————————————
// INPUT
// ———————————————————————————————————————
export function Input({
  label,
  error,
  hint,
  icon: Icon,
  type = 'text',
  style: customStyle,
  containerStyle,
  ...rest
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...containerStyle }}>
      {label && (
        <label style={{
          fontSize: 10, fontWeight: 700,
          color: 'var(--text-2)',
          textTransform: 'uppercase', letterSpacing: '.08em',
        }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {Icon && (
          <Icon
            size={14} strokeWidth={1.5}
            style={{
              position: 'absolute', left: 10, top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-3)', pointerEvents: 'none',
            }}
          />
        )}
        <style>{`
          .cf-input::placeholder { color: var(--text-3); opacity: 0.55; font-style: italic; font-weight: 400; }
          .cf-input::-webkit-input-placeholder { color: var(--text-3); opacity: 0.55; font-style: italic; font-weight: 400; }
        `}</style>
        <input
          type={type}
          className="cf-input"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%', height: 36,
            padding: Icon ? '0 12px 0 32px' : '0 12px',
            borderRadius: '8px 8px 0 0',
            border: 'none',
            borderBottom: `2px solid ${error ? 'var(--danger, #EF4444)' : focused ? 'var(--brand-s)' : 'var(--ghost-border)'}`,
            background: focused ? 'var(--bg-surface)' : 'var(--bg-elevated)',
            color: 'var(--text-1)',
            fontSize: 13, fontWeight: 400,
            fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
            boxShadow: focused ? '0 2px 12px rgba(4, 22, 39, 0.06)' : 'none',
            ...customStyle,
          }}
          {...rest}
        />
      </div>
      {error && (
        <span style={{ fontSize: 11, color: 'var(--danger, #EF4444)', fontWeight: 500 }}>
          {error}
        </span>
      )}
      {hint && !error && (
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ———————————————————————————————————————
// SKELETON — loading placeholders
// ———————————————————————————————————————
const skeletonAnim = `
  @keyframes cf-shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const skeletonStyle = {
  background: 'linear-gradient(90deg, var(--bg-elevated) 25%, var(--bg-hover) 50%, var(--bg-elevated) 75%)',
  backgroundSize: '800px 100%',
  animation: 'cf-shimmer 1.4s ease infinite',
  borderRadius: 6,
};

export function Skeleton({ width = '100%', height = 16, radius = 6, style: customStyle }) {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{ ...skeletonStyle, width, height, borderRadius: radius, ...customStyle }} />
    </>
  );
}

export function SkeletonText({ lines = 3, style: customStyle }) {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...customStyle }}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            style={{
              ...skeletonStyle,
              height: 12,
              width: i === lines - 1 ? '65%' : '100%',
              borderRadius: 4,
            }}
          />
        ))}
      </div>
    </>
  );
}

export function SkeletonAvatar({ size = 32, style: customStyle }) {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{
        ...skeletonStyle,
        width: size, height: size,
        borderRadius: '50%',
        flexShrink: 0,
        ...customStyle,
      }} />
    </>
  );
}

export function SkeletonPatientRow() {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--ghost-border)',
        borderRadius: 'var(--r-xl, 24px)',
        padding: '14px 18px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            <Skeleton width="45%" height={14} />
            <Skeleton width="60%" height={11} />
          </div>
          <Skeleton width={20} height={20} radius={4} style={{ marginLeft: 12 }} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Skeleton width={80} height={10} />
          <Skeleton width={110} height={10} />
          <Skeleton width={70} height={10} />
        </div>
      </div>
    </>
  );
}

export function SkeletonKPICard() {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--ghost-border)',
        borderRadius: 'var(--r-xl, 24px)',
        padding: '14px 16px',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <Skeleton width="50%" height={9} style={{ marginBottom: 10 }} />
        <Skeleton width="70%" height={28} radius={4} style={{ marginBottom: 8 }} />
        <Skeleton width="55%" height={10} />
      </div>
    </>
  );
}

export function SkeletonCard({ rows = 4, style: customStyle }) {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--ghost-border)',
        borderRadius: 'var(--r-xl, 24px)',
        overflow: 'hidden',
        ...customStyle,
      }}>
        <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--ghost-border)' }}>
          <Skeleton width="40%" height={10} />
        </div>
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array.from({ length: rows }).map((_, i) => (
            <Skeleton key={i} width={`${100 - i * 8}%`} height={12} />
          ))}
        </div>
      </div>
    </>
  );
}

// ———————————————————————————————————————
// EMPTY STATE
// ———————————————————————————————————————
export function EmptyState({
  icon: Icon,
  emoji,
  title,
  description,
  action,
  secondAction,
  style: customStyle,
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 32px',
      textAlign: 'center',
      ...customStyle,
    }}>
      {(Icon || emoji) && (
        <div style={{
          width: 56, height: 56,
          borderRadius: 16,
          background: 'var(--bg-elevated)',
          border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
          fontSize: emoji ? 24 : undefined,
        }}>
          {emoji || (Icon && <Icon size={24} strokeWidth={1.5} color="var(--text-3)" />)}
        </div>
      )}

      <div style={{
        fontSize: 15, fontWeight: 700,
        color: 'var(--text-1)',
        letterSpacing: '-.02em',
        marginBottom: 6,
        fontFamily: 'var(--font-display, "Manrope", system-ui, sans-serif)',
      }}>
        {title}
      </div>
      {description && (
        <div style={{
          fontSize: 13, fontWeight: 400,
          color: 'var(--text-2)',
          maxWidth: 320, lineHeight: 1.55,
          marginBottom: action ? 20 : 0,
          fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
        }}>
          {description}
        </div>
      )}

      {action && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            variant={action.variant || 'primary'}
            onClick={action.onClick}
            size="md"
          >
            {action.label}
          </Button>
          {secondAction && (
            <Button variant="ghost" onClick={secondAction.onClick} size="md">
              {secondAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ———————————————————————————————————————
// TOAST SYSTEM
// ———————————————————————————————————————
const ToastContext = createContext(null);

const TOAST_ICONS = {
  success: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  error: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  warning: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  info: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

const TOAST_STYLES = {
  success: { color: 'var(--success, #22C55E)',    bg: 'var(--success-sub, rgba(34,197,94,.1))',   border: 'var(--success-rim, rgba(34,197,94,.25))' },
  error:   { color: 'var(--danger, #EF4444)',     bg: 'var(--danger-sub, rgba(239,68,68,.1))',    border: 'var(--danger-rim, rgba(239,68,68,.25))' },
  warning: { color: 'var(--warning, #F59E0B)',    bg: 'var(--warning-sub, rgba(245,158,11,.1))',  border: 'var(--warning-rim, rgba(245,158,11,.25))' },
  info:    { color: '#60A5FA',                    bg: 'rgba(59,130,246,.1)',                      border: 'rgba(59,130,246,.25)' },
};

function ToastItem({ id, type = 'info', message, duration = 3500, onDismiss }) {
  const [exiting, setExiting] = useState(false);
  const s = TOAST_STYLES[type] || TOAST_STYLES.info;
  const Icon = TOAST_ICONS[type] || TOAST_ICONS.info;

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(id), 220);
  }, [id, onDismiss]);

  useEffect(() => {
    const t = setTimeout(dismiss, duration);
    return () => clearTimeout(t);
  }, [dismiss, duration]);

  return (
    <>
      <style>{`
        @keyframes cf-toast-in  { from { opacity:0; transform:translateX(100%) scale(.95); } to { opacity:1; transform:translateX(0) scale(1); } }
        @keyframes cf-toast-out { from { opacity:1; transform:translateX(0); }              to { opacity:0; transform:translateX(100%); } }
      `}</style>
      <div
        onClick={dismiss}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: 14,
          background: 'var(--bg-surface)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid var(--ghost-border)',
          boxShadow: 'var(--shadow-md)',
          minWidth: 240,
          maxWidth: 340,
          cursor: 'pointer',
          userSelect: 'none',
          animation: exiting ? 'cf-toast-out .22s ease forwards' : 'cf-toast-in .22s ease',
          fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
        }}
      >
        <div style={{
          width: 26, height: 26, borderRadius: 7, flexShrink: 0,
          background: s.bg,
          color: s.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon />
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', flex: 1, lineHeight: 1.4 }}>
          {message}
        </span>
        <div style={{ fontSize: 14, color: 'var(--text-3)', flexShrink: 0 }}>x</div>
      </div>
    </>
  );
}

export function ToastContainer() {
  const ctx = useContext(ToastContext);
  if (!ctx) return null;
  const { toasts, dismissToast } = ctx;

  return (
    <div className="cf-toast-container" style={{
      position: 'fixed',
      bottom: 20, right: 20,
      display: 'flex', flexDirection: 'column-reverse', gap: 8,
      zIndex: 9999,
      pointerEvents: 'none',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: 'all' }}>
          <ToastItem {...t} onDismiss={dismissToast} />
        </div>
      ))}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', message, duration = 3500 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, message, duration }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    _globalToast.dispatch = addToast;
    return () => { _globalToast.dispatch = null; };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, dismissToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de ToastProvider');
  return ctx;
}

// —— Singleton para usar toast sin hooks ——
const _globalToast = { dispatch: null };

export const toast = {
  success: (message, opts) => _globalToast.dispatch?.({ type: 'success', message, ...opts }),
  error:   (message, opts) => _globalToast.dispatch?.({ type: 'error',   message, ...opts }),
  warning: (message, opts) => _globalToast.dispatch?.({ type: 'warning', message, ...opts }),
  info:    (message, opts) => _globalToast.dispatch?.({ type: 'info',    message, ...opts }),
};

// ———————————————————————————————————————
// SECTION HEADER
// ———————————————————————————————————————
export function SectionHeader({ title, count, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{
          fontSize: 14, fontWeight: 700,
          color: 'var(--text-1)',
          letterSpacing: '-.02em', margin: 0,
          fontFamily: 'var(--font-display, "Manrope", system-ui, sans-serif)',
        }}>
          {title}
        </h2>
        {count !== undefined && (
          <Badge variant="neutral" style={{ fontSize: 10 }}>{count}</Badge>
        )}
      </div>
      {action}
    </div>
  );
}

// ———————————————————————————————————————
// CARD
// ———————————————————————————————————————
export function Card({ children, style: customStyle, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isClickable = !!onClick;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => isClickable && setHovered(true)}
      onMouseLeave={() => isClickable && setHovered(false)}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--ghost-border)',
        borderRadius: 'var(--r-xl, 24px)',
        overflow: 'hidden',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        transform: hovered && isClickable ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered && isClickable ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        ...customStyle,
      }}
    >
      {children}
    </div>
  );
}

// ———————————————————————————————————————
// SELECT
// ———————————————————————————————————————
export function Select({
  label,
  error,
  hint,
  options = [],
  placeholder = 'Seleccionar...',
  style: customStyle,
  containerStyle,
  ...rest
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...containerStyle }}>
      {label && (
        <label style={{
          fontSize: 10, fontWeight: 700,
          color: 'var(--text-2)',
          textTransform: 'uppercase', letterSpacing: '.08em',
        }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <select
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%', height: 36,
            padding: '0 32px 0 12px',
            borderRadius: '8px 8px 0 0',
            border: 'none',
            borderBottom: `2px solid ${error ? 'var(--danger)' : focused ? 'var(--brand-s)' : 'var(--ghost-border)'}`,
            background: focused ? 'var(--bg-surface)' : 'var(--bg-elevated)',
            color: 'var(--text-1)',
            fontSize: 13, fontWeight: 400,
            fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
            boxShadow: focused ? '0 2px 12px rgba(4, 22, 39, 0.06)' : 'none',
            appearance: 'none',
            cursor: 'pointer',
            ...customStyle,
          }}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map(opt => {
            const val = typeof opt === 'string' ? opt : opt.value;
            const lbl = typeof opt === 'string' ? opt : opt.label;
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </select>
        <svg
          width="12" height="12" viewBox="0 0 24 24"
          fill="none" stroke="var(--text-3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {error && <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 500 }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>{hint}</span>}
    </div>
  );
}

// ———————————————————————————————————————
// TEXTAREA
// ———————————————————————————————————————
export function Textarea({
  label,
  error,
  hint,
  rows = 3,
  style: customStyle,
  containerStyle,
  ...rest
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...containerStyle }}>
      {label && (
        <label style={{
          fontSize: 10, fontWeight: 700,
          color: 'var(--text-2)',
          textTransform: 'uppercase', letterSpacing: '.08em',
        }}>
          {label}
        </label>
      )}
      <textarea
        rows={rows}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: '8px 8px 0 0',
          border: 'none',
          borderBottom: `2px solid ${error ? 'var(--danger)' : focused ? 'var(--brand-s)' : 'var(--ghost-border)'}`,
          background: focused ? 'var(--bg-surface)' : 'var(--bg-elevated)',
          color: 'var(--text-1)',
          fontSize: 13, fontWeight: 400,
          fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
          boxShadow: focused ? '0 2px 12px rgba(4, 22, 39, 0.06)' : 'none',
          resize: 'vertical',
          lineHeight: 1.5,
          ...customStyle,
        }}
        {...rest}
      />
      {error && <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 500 }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>{hint}</span>}
    </div>
  );
}

// ———————————————————————————————————————
// CHECKBOX
// ———————————————————————————————————————
export function Checkbox({
  label,
  checked = false,
  onChange,
  disabled = false,
  style: customStyle,
}) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      userSelect: 'none',
      fontSize: 13, fontWeight: 400,
      color: 'var(--text-1)',
      fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
      ...customStyle,
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `1.5px solid ${checked ? 'var(--brand-s)' : 'var(--ghost-border)'}`,
        background: checked ? 'var(--brand-s)' : 'var(--bg-elevated)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.12s ease',
      }}>
        {checked && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      {label && <span>{label}</span>}
    </label>
  );
}

// ———————————————————————————————————————
// TABS
// ———————————————————————————————————————
export function Tabs({
  tabs = [],
  active,
  onChange,
  size = 'md',
  style: customStyle,
}) {
  const sizes = {
    sm: { h: 30, fs: 11, px: 10, gap: 2 },
    md: { h: 34, fs: 12, px: 14, gap: 3 },
    lg: { h: 40, fs: 13, px: 18, gap: 4 },
  };
  const s = sizes[size] || sizes.md;

  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        background: 'var(--bg-elevated)',
        border: 'none',
        borderRadius: 12,
        padding: s.gap,
        gap: s.gap,
        ...customStyle,
      }}
    >
      {tabs.map(tab => {
        const val = typeof tab === 'string' ? tab : tab.value;
        const lbl = typeof tab === 'string' ? tab : tab.label;
        const icon = typeof tab === 'object' ? tab.icon : null;
        const isActive = active === val;
        return (
          <button
            key={val}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(val)}
            style={{
              height: s.h,
              padding: `0 ${s.px}px`,
              fontSize: s.fs,
              fontWeight: isActive ? 700 : 500,
              borderRadius: 7,
              border: 'none',
              background: isActive ? 'var(--bg-surface)' : 'transparent',
              color: isActive ? 'var(--text-1)' : 'var(--text-3)',
              boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
              cursor: 'pointer',
              transition: 'all 0.12s ease',
              fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {icon && React.createElement(icon, { size: s.fs + 1, strokeWidth: 1.5 })}
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

// ———————————————————————————————————————
// MODAL
// Focus trap, ESC, overlay, aria
// ———————————————————————————————————————
export function Modal({
  open,
  onClose,
  title,
  children,
  width = 480,
  showClose = true,
  closeOnOverlay = true,
  footer,
  style: customStyle,
}) {
  const modalRef = useRef(null);
  const prevFocus = useRef(null);

  // Guardar foco previo y restaurar al cerrar
  useEffect(() => {
    if (open) {
      prevFocus.current = document.activeElement;
      // Pequeño delay para que el DOM se pinte
      requestAnimationFrame(() => {
        const first = modalRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        first?.focus();
      });
    } else if (prevFocus.current) {
      prevFocus.current.focus();
      prevFocus.current = null;
    }
  }, [open]);

  // ESC para cerrar
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Bloquear scroll del body
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes cf-modal-overlay { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cf-modal-in { from { opacity: 0; transform: scale(.96) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      `}</style>
      {/* Overlay */}
      <div
        onClick={closeOnOverlay ? onClose : undefined}
        style={{
          position: 'fixed', inset: 0,
          background: 'var(--bg-overlay)',
          zIndex: 'var(--z-modal, 30)',
          animation: 'cf-modal-overlay 0.18s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
      >
        {/* Panel */}
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-label={title || undefined}
          onClick={e => e.stopPropagation()}
          style={{
            background: 'var(--bg-surface)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--ghost-border)',
            borderRadius: 20,
            boxShadow: 'var(--shadow-lg)',
            width: '100%',
            maxWidth: width,
            maxHeight: 'calc(100vh - 32px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'cf-modal-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
            fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
            ...customStyle,
          }}
        >
          {/* Header */}
          {(title || showClose) && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid var(--ghost-border)',
              flexShrink: 0,
            }}>
              {title && (
                <h2 style={{
                  fontSize: 15, fontWeight: 700,
                  color: 'var(--text-1)',
                  letterSpacing: '-.02em', margin: 0,
                }}>
                  {title}
                </h2>
              )}
              {showClose && (
                <button
                  onClick={onClose}
                  aria-label="Cerrar"
                  style={{
                    width: 28, height: 28, borderRadius: 7,
                    border: 'none',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-2)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 400,
                    transition: 'background 0.12s ease',
                    flexShrink: 0, marginLeft: 12,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
          }}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              gap: 8,
              padding: '14px 20px',
              borderTop: '1px solid var(--ghost-border)',
              flexShrink: 0,
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ———————————————————————————————————————
// CONFIRM DIALOG
// Reemplaza window.confirm() nativo
// ———————————————————————————————————————
export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title = '¿Estás seguro?',
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  loading = false,
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      width={400}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && (
        <p style={{
          fontSize: 13, color: 'var(--text-2)',
          lineHeight: 1.55, margin: 0,
        }}>
          {message}
        </p>
      )}
    </Modal>
  );
}

// —— Singleton confirm() — uso: const ok = await confirm({ title, message }) ——
const _confirmState = { resolve: null, setState: null };

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, props: {} });

  useEffect(() => {
    _confirmState.setState = setState;
    return () => { _confirmState.setState = null; };
  }, []);

  const handleConfirm = () => {
    setState(s => ({ ...s, open: false }));
    _confirmState.resolve?.(true);
    _confirmState.resolve = null;
  };

  const handleCancel = () => {
    setState(s => ({ ...s, open: false }));
    _confirmState.resolve?.(false);
    _confirmState.resolve = null;
  };

  return (
    <>
      {children}
      <ConfirmDialog
        open={state.open}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        {...state.props}
      />
    </>
  );
}

export const confirm = (props = {}) => {
  return new Promise((resolve) => {
    _confirmState.resolve = resolve;
    _confirmState.setState?.({
      open: true,
      props: typeof props === 'string' ? { message: props } : props,
    });
  });
};

// ———————————————————————————————————————
// SKELETON — extras para más páginas
// ———————————————————————————————————————
export function SkeletonTable({ cols = 4, rows = 5 }) {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--ghost-border)',
        borderRadius: 16,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 12,
          padding: '12px 16px',
          borderBottom: '1px solid var(--ghost-border)',
        }}>
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} width={`${60 + Math.random() * 30}%`} height={9} />
          ))}
        </div>
        {/* Rows */}
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 12,
            padding: '14px 16px',
            borderBottom: r < rows - 1 ? '1px solid var(--border-soft, var(--border))' : 'none',
          }}>
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton key={c} width={`${50 + Math.random() * 40}%`} height={12} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

export function SkeletonCalendar() {
  return (
    <>
      <style>{skeletonAnim}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Header bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton width={200} height={20} />
          <div style={{ display: 'flex', gap: 6 }}>
            <Skeleton width={60} height={30} radius={8} />
            <Skeleton width={60} height={30} radius={8} />
            <Skeleton width={60} height={30} radius={8} />
          </div>
        </div>
        {/* Col headers */}
        <div style={{
          display: 'grid', gridTemplateColumns: '48px repeat(5, 1fr)', gap: 4,
        }}>
          <div />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} width="80%" height={14} style={{ margin: '0 auto' }} />
          ))}
        </div>
        {/* Time rows */}
        {Array.from({ length: 8 }).map((_, r) => (
          <div key={r} style={{
            display: 'grid', gridTemplateColumns: '48px repeat(5, 1fr)', gap: 4, alignItems: 'start',
          }}>
            <Skeleton width={28} height={10} />
            {Array.from({ length: 5 }).map((_, c) => (
              <Skeleton key={c} height={r % 3 === 0 ? 48 : 32} radius={6} style={{ opacity: 0.5 + Math.random() * 0.5 }} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
